import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const TIERS = [
  { value: "free", label: "Free", color: "slate" },
  { value: "monthly", label: "Monthly ($49)", color: "blue" },
  { value: "quarterly", label: "Quarterly ($129)", color: "purple" },
  { value: "annual", label: "Annual ($399)", color: "emerald" },
];

const STATUSES = [
  { value: "inactive", label: "Inactive", color: "slate" },
  { value: "active", label: "Active", color: "emerald" },
  { value: "cancelled", label: "Cancelled", color: "red" },
];

export default function Admin() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ tier: "monthly", status: "active" });
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterTier, setFilterTier] = useState("all");
  const [stats, setStats] = useState({ total: 0, active: 0, free: 0, revenue: 0 });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data);
      calculateStats(res.data);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error("Admin access required");
      } else {
        toast.error("Failed to fetch users");
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (userData) => {
    const total = userData.length;
    const active = userData.filter(u => u.subscription_status === "active").length;
    const free = userData.filter(u => u.subscription_tier === "free" || !u.subscription_tier).length;
    
    // Calculate estimated monthly revenue
    let revenue = 0;
    userData.forEach(u => {
      if (u.subscription_status === "active") {
        if (u.subscription_tier === "monthly") revenue += 49;
        else if (u.subscription_tier === "quarterly") revenue += 43; // ~$129/3
        else if (u.subscription_tier === "annual") revenue += 33; // ~$399/12
      }
    });
    
    setStats({ total, active, free, revenue: Math.round(revenue) });
  };

  const handleActivate = (userToEdit) => {
    setSelectedUser(userToEdit);
    setFormData({
      tier: userToEdit.subscription_tier || "monthly",
      status: userToEdit.subscription_status || "active"
    });
    setShowModal(true);
  };

  const handleSaveSubscription = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      await axios.post(`${API}/admin/subscription/activate`, {
        user_email: selectedUser.email,
        tier: formData.tier,
        status: formData.status
      });
      toast.success(`Subscription updated for ${selectedUser.name}`);
      setShowModal(false);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update subscription");
    } finally {
      setSaving(false);
    }
  };

  const handleQuickActivate = async (userToActivate, tier) => {
    try {
      await axios.post(`${API}/admin/subscription/activate`, {
        user_email: userToActivate.email,
        tier: tier,
        status: "active"
      });
      toast.success(`${userToActivate.name} activated on ${tier} plan!`);
      fetchUsers();
    } catch (err) {
      toast.error("Failed to activate");
    }
  };

  const handleDeactivate = async (userToDeactivate) => {
    if (!window.confirm(`Deactivate subscription for ${userToDeactivate.name}?`)) return;
    try {
      await axios.post(`${API}/admin/subscription/activate`, {
        user_email: userToDeactivate.email,
        tier: "free",
        status: "inactive"
      });
      toast.success(`Subscription deactivated for ${userToDeactivate.name}`);
      fetchUsers();
    } catch (err) {
      toast.error("Failed to deactivate");
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          u.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesTier = filterTier === "all" || 
                        (filterTier === "free" && (!u.subscription_tier || u.subscription_tier === "free")) ||
                        (filterTier === "paid" && u.subscription_status === "active");
    return matchesSearch && matchesTier;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getStatusColor = (status) => {
    if (status === "active") return "text-emerald-500 bg-emerald-500/10 border-emerald-500/30";
    if (status === "cancelled") return "text-red-500 bg-red-500/10 border-red-500/30";
    return "text-slate-500 bg-slate-500/10 border-slate-500/30";
  };

  const getTierColor = (tier) => {
    if (tier === "annual") return "text-emerald-500";
    if (tier === "quarterly") return "text-purple-500";
    if (tier === "monthly") return "text-blue-500";
    return "text-slate-500";
  };

  return (
    <div className="p-6 space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-mono font-bold text-white">ADMIN PANEL</h1>
          <p className="text-sm text-slate-500 font-mono">Manage subscriptions & users</p>
        </div>
        <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-mono text-emerald-500">ADMIN ACCESS</span>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="card-institutional p-4">
          <p className="text-xs font-mono text-slate-500 mb-1">TOTAL USERS</p>
          <p className="text-3xl font-mono font-bold text-white">{stats.total}</p>
        </div>
        <div className="card-institutional p-4">
          <p className="text-xs font-mono text-slate-500 mb-1">ACTIVE SUBSCRIBERS</p>
          <p className="text-3xl font-mono font-bold text-emerald-500">{stats.active}</p>
        </div>
        <div className="card-institutional p-4">
          <p className="text-xs font-mono text-slate-500 mb-1">FREE TIER</p>
          <p className="text-3xl font-mono font-bold text-yellow-500">{stats.free}</p>
        </div>
        <div className="card-institutional p-4">
          <p className="text-xs font-mono text-slate-500 mb-1">EST. MONTHLY REVENUE</p>
          <p className="text-3xl font-mono font-bold text-blue-500">${stats.revenue}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full input-institutional px-4 py-3"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-2">
          {["all", "paid", "free"].map((filter) => (
            <button
              key={filter}
              onClick={() => setFilterTier(filter)}
              className={`px-4 py-2 font-mono text-sm border transition-colors ${
                filterTier === filter
                  ? "bg-blue-500/20 border-blue-500 text-blue-500"
                  : "border-[#262626] text-slate-400 hover:text-white"
              }`}
            >
              {filter.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="card-institutional overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#262626]">
                {["USER", "EMAIL", "TIER", "STATUS", "EXPIRES", "JOINED", "ACTIONS"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-mono text-slate-500 uppercase tracking-wider">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500 font-mono">
                    Loading users...
                  </td>
                </tr>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-slate-500 font-mono">
                    No users found
                  </td>
                </tr>
              ) : (
                filteredUsers.map((u) => (
                  <tr key={u.id} className="border-b border-[#262626] hover:bg-[#171717] transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
                          <span className="text-blue-500 font-mono text-sm">{u.name?.[0]?.toUpperCase()}</span>
                        </div>
                        <span className="font-mono text-white">{u.name}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-400">{u.email}</td>
                    <td className="px-4 py-3">
                      <span className={`font-mono text-sm ${getTierColor(u.subscription_tier)}`}>
                        {(u.subscription_tier || "free").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-mono px-2 py-1 border ${getStatusColor(u.subscription_status)}`}>
                        {(u.subscription_status || "inactive").toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-400">
                      {formatDate(u.subscription_expires)}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-slate-500">
                      {formatDate(u.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {u.subscription_status !== "active" ? (
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleQuickActivate(u, "monthly")}
                              className="text-xs font-mono px-2 py-1 bg-blue-500/10 border border-blue-500/30 text-blue-500 hover:bg-blue-500/20"
                              title="Quick activate monthly"
                            >
                              M
                            </button>
                            <button
                              onClick={() => handleQuickActivate(u, "quarterly")}
                              className="text-xs font-mono px-2 py-1 bg-purple-500/10 border border-purple-500/30 text-purple-500 hover:bg-purple-500/20"
                              title="Quick activate quarterly"
                            >
                              Q
                            </button>
                            <button
                              onClick={() => handleQuickActivate(u, "annual")}
                              className="text-xs font-mono px-2 py-1 bg-emerald-500/10 border border-emerald-500/30 text-emerald-500 hover:bg-emerald-500/20"
                              title="Quick activate annual"
                            >
                              A
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleDeactivate(u)}
                            className="text-xs font-mono text-red-500 hover:text-red-400"
                          >
                            DEACTIVATE
                          </button>
                        )}
                        <button
                          onClick={() => handleActivate(u)}
                          className="text-xs font-mono text-blue-500 hover:text-blue-400"
                        >
                          EDIT
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card-institutional p-6">
        <h3 className="font-mono text-sm font-bold text-white mb-4">QUICK REFERENCE</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-slate-500 mb-2">Quick Activate Buttons:</p>
            <div className="space-y-1">
              <p className="text-slate-400"><span className="text-blue-500 font-mono">M</span> = Monthly ($49/mo)</p>
              <p className="text-slate-400"><span className="text-purple-500 font-mono">Q</span> = Quarterly ($129/3mo)</p>
              <p className="text-slate-400"><span className="text-emerald-500 font-mono">A</span> = Annual ($399/yr)</p>
            </div>
          </div>
          <div>
            <p className="text-slate-500 mb-2">After Zelle Payment:</p>
            <ol className="space-y-1 text-slate-400 list-decimal list-inside">
              <li>Find user by email</li>
              <li>Click M, Q, or A button</li>
              <li>User gets instant access</li>
            </ol>
          </div>
          <div>
            <p className="text-slate-500 mb-2">Zelle Info:</p>
            <p className="text-slate-400">investolife2019@gmail.com</p>
            <p className="text-slate-500 text-xs mt-1">Users should include their email in memo</p>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-[#0A0A0A] border-[#262626] max-w-md">
          <DialogHeader>
            <DialogTitle className="font-mono text-white">EDIT SUBSCRIPTION</DialogTitle>
          </DialogHeader>
          
          {selectedUser && (
            <div className="space-y-4 mt-4">
              <div className="bg-[#050505] border border-[#262626] p-4">
                <p className="text-xs font-mono text-slate-500 mb-1">USER</p>
                <p className="font-mono text-white">{selectedUser.name}</p>
                <p className="text-sm text-slate-400">{selectedUser.email}</p>
              </div>

              <div>
                <label className="block text-xs font-mono text-slate-500 mb-2">SUBSCRIPTION TIER</label>
                <Select value={formData.tier} onValueChange={(v) => setFormData({ ...formData, tier: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#262626] font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                    {TIERS.map((t) => (
                      <SelectItem key={t.value} value={t.value} className="font-mono">{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-xs font-mono text-slate-500 mb-2">STATUS</label>
                <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                  <SelectTrigger className="bg-[#050505] border-[#262626] font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-[#262626]">
                    {STATUSES.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="font-mono">{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  onClick={() => setShowModal(false)}
                  className="flex-1 btn-secondary"
                >
                  CANCEL
                </button>
                <button
                  onClick={handleSaveSubscription}
                  disabled={saving}
                  className="flex-1 btn-primary"
                >
                  {saving ? "SAVING..." : "SAVE CHANGES"}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
